/**
 * useGeolocation Hook
 * 
 * React hook for managing background geolocation tracking.
 * Provides easy integration with the GeolocationService.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { geolocationService } from '../services/GeolocationService';

export function useGeolocation() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [isNativePlatform, setIsNativePlatform] = useState(false);
  const [autoStartEnabled, setAutoStartEnabled] = useState(false);
  const [currentTrip, setCurrentTrip] = useState(null);
  const [lastLocation, setLastLocation] = useState(null);
  const [error, setError] = useState(null);
  const [tripHistory, setTripHistory] = useState([]);
  const [dailyStats, setDailyStats] = useState({ tripsToday: 0, milesToday: 0 });
  const mountedRef = useRef(true);

  // Initialize the service
  useEffect(() => {
    let cleanup = null;

    const init = async () => {
      const success = await geolocationService.initialize();
      if (mountedRef.current) {
        setIsInitialized(success);
        setIsNativePlatform(geolocationService.isNativePlatform);
        
        // Update initial status
        const status = geolocationService.getStatus();
        setIsTracking(status.isTracking);
        setLastLocation(status.lastLocation);
        setCurrentTrip(status.currentTrip);
        setAutoStartEnabled(status.autoStartEnabled);
        setDailyStats(geolocationService.getDailyStats());
      }
    };

    // Add listener for geolocation events
    cleanup = geolocationService.addListener((event, data) => {
      if (!mountedRef.current) return;

      switch (event) {
        case 'tracking_started':
          setIsTracking(true);
          setError(null);
          break;
        case 'tracking_stopped':
          setIsTracking(false);
          setCurrentTrip(null);
          break;
        case 'location_update':
          setLastLocation(data.point);
          break;
        case 'trip_started':
          setCurrentTrip({
            id: data.tripId,
            startLocation: data.startLocation,
            pointCount: 1,
            totalDistance: 0,
          });
          break;
        case 'trip_progress':
          setCurrentTrip(prev => prev ? {
            ...prev,
            pointCount: data.pointCount,
            totalDistance: data.totalDistance,
            distanceMiles: data.totalDistance / 1609.34,
          } : null);
          break;
        case 'trip_ended':
          setCurrentTrip(null);
          setTripHistory(prev => [data, ...prev].slice(0, 10)); // Keep last 10
          setDailyStats(geolocationService.getDailyStats());
          break;
        case 'trip_discarded':
          setCurrentTrip(null);
          break;
        case 'permission_denied':
          setError({ type: 'permission', message: 'Location permission denied' });
          setIsTracking(false);
          break;
        case 'autostart_changed':
          setAutoStartEnabled(data.enabled);
          break;
        default:
          break;
      }
    });

    init();

    return () => {
      mountedRef.current = false;
      if (cleanup) cleanup();
    };
  }, []);

  // Start tracking
  const startTracking = useCallback(async () => {
    setError(null);
    const result = await geolocationService.startTracking();
    if (!result.success) {
      setError({ type: 'start_failed', message: result.reason, details: result.error });
    }
    return result;
  }, []);

  // Stop tracking
  const stopTracking = useCallback(async () => {
    const result = await geolocationService.stopTracking();
    if (!result.success) {
      setError({ type: 'stop_failed', details: result.error });
    }
    return result;
  }, []);

  // Toggle tracking
  const toggleTracking = useCallback(async () => {
    if (isTracking) {
      return stopTracking();
    } else {
      return startTracking();
    }
  }, [isTracking, startTracking, stopTracking]);

  // Toggle auto-start
  const toggleAutoStart = useCallback(() => {
    const newValue = !autoStartEnabled;
    geolocationService.setAutoStart(newValue);
    setAutoStartEnabled(newValue);
  }, [autoStartEnabled]);

  // Open settings
  const openSettings = useCallback(async () => {
    await geolocationService.openSettings();
  }, []);

  // Retry failed trips
  const retryFailedTrips = useCallback(async () => {
    return geolocationService.retryFailedTrips();
  }, []);

  // Get status
  const getStatus = useCallback(() => {
    return geolocationService.getStatus();
  }, []);

  // Get failed trips count
  const getFailedTripsCount = useCallback(() => {
    return geolocationService.getFailedTripsCount();
  }, []);

  return {
    // State
    isInitialized,
    isNativePlatform,
    isTracking,
    autoStartEnabled,
    currentTrip,
    lastLocation,
    error,
    tripHistory,
    dailyStats,
    // Actions
    startTracking,
    stopTracking,
    toggleTracking,
    toggleAutoStart,
    openSettings,
    retryFailedTrips,
    getStatus,
    getFailedTripsCount,
  };
}

export default useGeolocation;
